/**
 * The call-off list's query, in one place.
 *
 * The list and its export both read it, so the file cannot hold different
 * loads from the screen it came from (CR-7732).
 */
import { statusFilterParams, type CallOffBucket } from './callOffListConfig';

export interface CallOffListFilters {
  isHaulier: boolean;
  activeFilter: CallOffBucket;
  statusFilter: string;
  asnStageFilter: string;
  search: string;
  supplierFilter: string;
  ordering: string;
}

/**
 * The server filters for the list as it is showing, without paging.
 *
 * @param filters - the list's current filter state
 * @returns the query parameters for `GET call-off/call-offs/` (and its export)
 */
export function buildCallOffListParams(filters: CallOffListFilters): Record<string, string> {
  const params: Record<string, string> = {};
  // Map the bucket toggle to the right backend filter:
  //   active    → status=active (covers draft/pending/confirmed/dispatched)
  //   completed → detail_status=delivered (only successfully delivered)
  //   cancelled → detail_status=cancelled
  // CR-7732: a haulier has no bucket toggle, so it must not be pinned to
  // one. Defaulting it to 'active' would have hidden exactly the loads it
  // has finished — delivered and cancelled — from the only list it has.
  // Its scope is already narrowed server-side to the states it may see.
  if (!filters.isHaulier) {
    if (filters.activeFilter === 'active') params['status'] = 'active';
    else if (filters.activeFilter === 'completed') params['detail_status'] = 'delivered';
    else params['detail_status'] = 'cancelled';
  }
  // statusFilter (stat-card drill-in) overrides the toggle's detail_status
  // default — including the derived filters some cards carry (CR-7781:
  // Logistics Confirmed; Dispatched excluding loads counted by later cards).
  Object.assign(params, statusFilterParams(filters.statusFilter));
  if (filters.asnStageFilter) params['asn_stage'] = filters.asnStageFilter;
  if (filters.search) params['search'] = filters.search;
  if (filters.supplierFilter) params['supplier'] = filters.supplierFilter;
  if (filters.ordering) params['ordering'] = filters.ordering;
  return params;
}
